
  import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
  import vSelect from 'vue-select';
  import JobCard from '@/views/job/_shared/JobCard.vue';
  import debounce from 'lodash/debounce';
  import { Validations } from 'vuelidate-property-decorators';
  import { required, minValue, maxLength } from 'vuelidate/lib/validators';
  import {
    Client,
    IJobModel,
    IJobItemModel,
    IJobItemActivityModel,
    ILocationModel,
    IJobItemScheduleModel,
    JobItemByNumberSearchResultModel,
    JobItemActivityModel,
    EmployeeModel
  } from '@/services/api.service';
  import {
    JobItemType,
    Status,
    JobType,
    AreaType,
    EnumModel,
    StatusEnums,
    JobTypeEnums,
    JobItemTypeEnums,
    AreaTypeEnums,
    ActivityInvoiceType,
    ActvityInvoiceTypeEnums
  } from '@/common/enumerations';

  import toJobItemTypeFormatter from '@/filters/jobitemtype.formatter';
  import toStatusFormatter from '@/filters/status.formatter';
  import toISODateFormatter from '@/filters/toISOdate.formatter';
  import toDateFormatter from '@/filters/todate.formatter';
  import toHoursMinutesFormatter from '@/filters/tohoursminutes.formatter';
  import * as StringUtils from '@/utilities/string.utils';

  // Custom validators
  function validsitetime(value: any) {
    try {
      const val = parseFloat(value);
      return val > 0 && val < 1440;
    } catch (e) {
      return false;
    }
  }

  @Component({
    components: {
      vSelect,
      JobCard
    },
    filters: {
      toJobItemType: toJobItemTypeFormatter,
      toStatus: toStatusFormatter,
      toISODate: toISODateFormatter,
      toDate: toDateFormatter,
      toHoursMinutes: toHoursMinutesFormatter
    }
  })
  export default class JobActivityQuickEntry extends Vue {
    private clientName: string = '';

    private job: IJobModel | null = null;
    private jobItems: IJobItemModel[] = [];
    private jobItem: IJobItemModel | null = null;
    private locations: ILocationModel[] = [];
    private activities: IJobItemActivityModel[] | null = null;

    private jobLoaded: boolean = false;
    private jobItemsLoaded: boolean = false;
    private jobItemLoaded: boolean = false;
    private locationsLoaded: boolean = false;
    private activitiesLoaded: boolean = false;

    private jobItemNumber: string = '';
    private jobItemName: string = '';
    private selectedJobItem: JobItemByNumberSearchResultModel | null = null;
    private matchingJobItems: JobItemByNumberSearchResultModel[] = [];

    private employeeGid: string = '';
    private employees: EmployeeModel[] = [];
    private employeesPlaceholder: EmployeeModel | null = null;

    private activityDate: Date = new Date();

    private enteredSiteTimeInHours: number = 0;
    private enteredSiteTimeInMinutes: number = 0;
    private enteredTravelTimeInHours: number = 0;
    private enteredTravelTimeInMinutes: number = 0;

    private notes: string = '';
    private isAdminActivity: boolean = false;

    private availableInvoiceTypes: EnumModel[] = [];
    private selectedInvoiceType: number = 0;

    // Form state handling
    private formIsValid: boolean | null = null;
    private enableActivityEntry: boolean = false;

    @Validations()
    private validations = {
      activityDate: { required },
      job: { required },
      jobItem: { required }
    };

    protected mounted() {
      this.availableInvoiceTypes.push(...ActvityInvoiceTypeEnums);
      // @ts-ignore
      this.$Progress.start();
      // set the initial date to yesterday
      this.activityDate.setDate(this.activityDate.getDate() - 1);
      this.fetchBaseData();
    }

    private async fetchBaseData() {
      this.fetchEmployees();
    }

    private fetchEmployees() {
      const clientService: Client = new Client(process.env.VUE_APP_API_URL, this.$http);

      const employees = clientService.getEmployees().then(data => {
        const sortedData = data.sort((n1, n2) => StringUtils.stringCompare(n1.firstName || '', n2.firstName || ''));
        const employeesPlaceholder = new EmployeeModel();
        employeesPlaceholder.init({ firstName: '(Select Employee...)', lastName: '', gid: '' });
        sortedData.unshift(employeesPlaceholder);

        this.employees = sortedData;
      });
    }
    private searchForJobItems(search: string, loading: (show: boolean) => any) {
      if (search !== undefined && search !== null && search !== '') {
        loading(true);
        this.internalSearchForItems(search, loading, this);
      }
    }

    // Taken from: https://stackoverflow.com/questions/40686767/using-lodash-debounce-in-angular2-typescript-scroll-not-working
    private internalSearchForItems = debounce((search, loading, vm) => {
      const clientService = new Client(process.env.VUE_APP_API_URL, this.$http);
      clientService.searchJobItems(search).then(data => {
        // push the results into the list
        vm.matchingJobItems = data;
        loading(false);
      });
    }, 300);

    private async fetchJobInformation(jobGid: string, jobItemGid: string) {
      // @ts-ignore
      this.$Progress.start();
      const clientService = new Client(process.env.VUE_APP_API_URL, this.$http);
      const [theJob, theJobItems, theLocations, theActivities] = await Promise.all([
        clientService.getJob(jobGid),
        clientService.getJobItems(jobGid),
        clientService.getJobLocations(jobGid),
        clientService.getJobActivities(jobGid)
      ]);

      this.job = theJob;
      this.jobItems = theJobItems;
      this.locations = theLocations;
      this.activities = theActivities;

      // locate the selected job item and set that
      const tempJobItem = this.jobItems.find(t => t.gid === jobItemGid);
      if (tempJobItem !== undefined) {
        this.jobItem = tempJobItem;
      }
      this.jobLoaded = true;
      this.jobItemLoaded = true;
      this.jobItemsLoaded = true;
      this.locationsLoaded = true;
      this.activitiesLoaded = true;
      // @ts-ignore
      this.$Progress.finish();
    }

    private cancelEdit() {
      console.log('Resetting form....');
      this.resetForm();
    }

    private createJobActivity() {
      // Do the work here
      // Run the validation
      // @ts-ignore
      this.$v.$touch();
      // @ts-ignore
      if (this.$v.$invalid) {
        this.formIsValid = false;
      } else {
        // do your submit logic here
        this.formIsValid = true;
        const jobGid = '';
        const jobItemGid = '';

        // Need to force the vars to be numbers cos fuck javascript
        // The + char is a shorthand cast to number
        const siteTime: number = +(this.enteredSiteTimeInHours * 60) + +this.enteredSiteTimeInMinutes;
        let travelTime: number = +(this.enteredTravelTimeInHours * 60) + +this.enteredTravelTimeInMinutes;
        // Reset the travel time to 0 if it is an admin activity
        if (this.isAdminActivity) {
          travelTime = 0;
        }

        if (this.job != null && this.jobItem != null) {
          const newActivity: JobItemActivityModel = new JobItemActivityModel();
          newActivity.init({
            employeeGid: this.employeeGid,
            jobItemGid: this.jobItem.gid,
            activityDateTime: this.activityDate,
            siteTimeInMinutes: siteTime,
            travelTimeInMinutes: travelTime,
            notes: this.notes,
            isAdminActivity: this.isAdminActivity,
            invoiceType: this.selectedInvoiceType,
          });

          const clientService: Client = new Client(process.env.VUE_APP_API_URL, this.$http);
          // @ts-ignore
          this.$Progress.start();
          clientService
            .createJobItemActivity(this.job.gid!, this.jobItem.gid!, newActivity)
            .then((data: JobItemActivityModel) => {
              // Route to the edit job list screen?
              // @ts-ignore
              this.$Progress.finish();
              // @ts-ignore
              this.$awn.success('Job Activity for ' + this.jobItem.jobItemNumber + ' created successfully');
              // For the moment, nav to the client screen
              // this.$router.push({ path: '/jobs/view/' + data.gid });
              // Reset the from
              this.cancelEdit();
            });
        }
      }
    }

    private resetForm() {
      this.jobLoaded = false;
      this.jobItemsLoaded = false;
      this.jobItemLoaded = false;
      this.locationsLoaded = false;
      this.activitiesLoaded = false;
      this.jobItemNumber = '';
      this.jobItemName = '';
      this.selectedJobItem = null;
      this.matchingJobItems = [];
      this.jobItems = [];
      this.job = null;
      this.jobItem = null;

      this.notes = '';
      this.enteredSiteTimeInHours = 0;
      this.enteredSiteTimeInMinutes = 0;
      this.enteredTravelTimeInHours = 0;
      this.enteredTravelTimeInMinutes = 0;
      this.selectedInvoiceType = 0;
      this.employeeGid = '';
      this.activityDate = new Date();
      this.isAdminActivity = false;
      // set the initial date to yesterday
      this.activityDate.setDate(this.activityDate.getDate() - 1);

      this.enableActivityEntry = false;
    }

    @Watch('selectedJobItem')
    private onSelectedJobItemChanged(
      value: JobItemByNumberSearchResultModel | null,
      oldValue: JobItemByNumberSearchResultModel | null
    ) {
      // does not require underlying data set to change
      console.log('selected job item changed');
      if (value != null && value !== undefined) {
        this.fetchJobInformation(value.jobGid || '', value.jobItemGid || '');
        this.enableActivityEntry = true;
      }
    }

    private getStatus(value: number) {
      return toStatusFormatter(value).toLowerCase();
    }
  }
